import React, { useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  CalculatorIcon,
  CurrencyDollarIcon,
  PaintBrushIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  BellIcon,
  CreditCardIcon,
  QuestionMarkCircleIcon,
  MapPinIcon,
  MegaphoneIcon,
  UsersIcon,
  TagIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  FolderIcon,
  BuildingStorefrontIcon,
  ChevronDownIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useDealerWorkspace, useSettingsHref } from '../admin/DealerWorkspaceContext';
import RouteErrorBoundary from '../ui/RouteErrorBoundary';
import { useOptionalAuth } from '../../contexts/AuthContext';
import { canManageUsers } from './UsersRolesPage';

const NAV_GROUPS = [
  {
    label: 'Company & Team',
    items: [
      { label: 'Company Profile', path: '/settings/company', icon: BuildingOfficeIcon },
      { label: 'Users & Roles', path: '/settings/users', icon: UserGroupIcon },
      { label: 'Branding', path: '/settings/branding', icon: PaintBrushIcon },
      { label: 'Crews', path: '/settings/crews', icon: UserCircleIcon },
    ],
  },
  {
    // Iter18: removed 'Item Catalog' (super-admin surface, not tenant-facing) and
    // 'Coverage Charts' (now surfaced as a per-product CTA inside Quote Items).
    label: 'Quoting & Pricing',
    items: [
      { label: 'Margins & Labor', path: '/settings/margins', icon: CalculatorIcon },
      { label: 'Item Catalog', path: '/settings/quote-items', icon: TagIcon },
      { label: 'Tax & Rates', path: '/settings/tax', icon: CurrencyDollarIcon },
      { label: 'Quote Defaults', path: '/settings/quote-defaults', icon: DocumentTextIcon },
      { label: 'Templates', path: '/settings/quote-templates', icon: ClipboardDocumentListIcon },
      { label: 'T&C Templates', path: '/settings/tc-templates', icon: DocumentTextIcon },
      { label: 'Quote Areas', path: '/settings/quote-areas', icon: MapPinIcon },
    ],
  },
  {
    label: 'Job & Customer',
    items: [
      { label: 'Lead Sources', path: '/settings/lead-sources', icon: MegaphoneIcon },
      { label: 'Competitors & Lost Reasons', path: '/settings/competitors', icon: UsersIcon },
      { label: 'Customer Types', path: '/settings/customer-types', icon: TagIcon },
      { label: 'Jobs & Work Orders', path: '/settings/job-types', icon: WrenchScrewdriverIcon },
      { label: 'Resource Types', path: '/settings/resource-types', icon: TruckIcon },
      { label: 'Vendors & Warehouses', path: '/settings/vendors', icon: BuildingStorefrontIcon },
    ],
  },
  {
    label: 'Account & Support',
    items: [
      { label: 'Notifications', path: '/settings/notifications', icon: BellIcon },
      { label: 'Subscription & Billing', path: '/settings/billing', icon: CreditCardIcon },
      { label: 'Help & Support', path: '/settings/support', icon: QuestionMarkCircleIcon },
      { label: 'Files', path: '/settings/documents', icon: FolderIcon },
    ],
  },
];

// IDI-648 (D6): a signed-in user who may not change settings sees only these
// areas; any other Settings address opens Templates. No user (or no
// AuthProvider): nothing is hidden. The server is the boundary.
const LIMITED_PATHS = ['/settings/users', '/settings/quote-templates', '/settings/support'];
const LIMITED_LANDING = '/settings/quote-templates';

function useSettingsLimited() {
  const auth = useOptionalAuth();
  const user = auth?.user;
  return Boolean(user) && !canManageUsers(user);
}

function useNavGroups() {
  const limited = useSettingsLimited();
  if (!limited) return NAV_GROUPS;
  return NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => LIMITED_PATHS.includes(i.path)) }))
    .filter((g) => g.items.length > 0);
}

function MobileSettingsNav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const href = useSettingsHref(); // IDI-63: rail stays inside a dealer workspace
  const navGroups = useNavGroups();

  const currentItem = navGroups.flatMap(g => g.items).find(
    item => location.pathname === href(item.path) || location.pathname.startsWith(href(item.path) + '/')
  );

  return (
    <div className="lg:hidden mb-4">
      <button
        onClick={() => setOpen(prev => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-[color:var(--color-border)] text-sm font-medium text-gray-700" style={{ borderRadius: 'var(--radius-md)' }}
        aria-expanded={open}
        aria-controls="settings-subnav-mobile"
      >
        <span>{currentItem?.label || 'Settings'}</span>
        <ChevronDownIcon className={clsx('h-5 w-5 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <nav
          id="settings-subnav-mobile"
          className="mt-2 bg-white border border-[color:var(--color-border)] overflow-hidden z-20 relative" style={{ borderRadius: 'var(--radius-md)' }}
          role="navigation"
          aria-label="Settings navigation"
        >
          {navGroups.map(group => (
            <div key={group.label}>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                {group.label}
              </div>
              {group.items.map(item => (
                <NavLink
                  key={item.path}
                  to={href(item.path)}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                      isActive
                        ? 'bg-primary-50 text-primary-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    )
                  }
                >
                  <item.icon className="h-4 w-4 flex-shrink-0" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      )}
    </div>
  );
}

function DesktopSettingsNav() {
  const href = useSettingsHref();
  const navGroups = useNavGroups();
  return (
    <aside
      className="hidden lg:block w-64 flex-shrink-0 border-r border-[color:var(--color-border)] bg-white overflow-y-auto"
      role="navigation"
      aria-label="Settings navigation"
    >
      <div className="py-4">
        {navGroups.map(group => (
          <div key={group.label} className="mb-4">
            <h3 className="px-4 mb-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {group.label}
            </h3>
            <ul className="space-y-0.5">
              {group.items.map(item => (
                <li key={item.path}>
                  <NavLink
                    to={href(item.path)}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-3 px-4 py-2 text-sm rounded-md mx-2 transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      )
                    }
                  >
                    <item.icon className="h-4 w-4 flex-shrink-0" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </aside>
  );
}

export default function SettingsLayout() {
  const location = useLocation();
  const workspace = useDealerWorkspace();
  const href = useSettingsHref();
  const limited = useSettingsLimited();
  const allowed = !limited || LIMITED_PATHS.some((p) => {
    const full = href(p);
    return location.pathname === full || location.pathname.startsWith(full + '/');
  });
  // Iter18: the parent <main> has p-4 (mobile) / md:p-6 padding. We negate that
  // padding with negative margin so the settings rail can span edge-to-edge,
  // then reclaim the same vertical space via min-h calc so the rail extends to
  // the bottom of the viewport (previously it stopped short by 2× padding).
  //
  // R18/AM-1 follow-up (2026-07-31 evening): <main>'s bottom padding moved to
  // the inner content wrapper so sticky footers can render flush to viewport.
  // <main> now has `pt-4 md:pt-6 px-4 md:px-6` (top+horizontal only), and the
  // content wrapper has `pb-4 md:pb-6`. To preserve edge-to-edge bleed on all
  // three needed sides, we negate top+horizontal against <main> and bottom
  // against the wrapper. The three separate `-m*-*` classes below map to the
  // three padding sources; a bare `-m-4 md:-m-6` would over-bleed on the sides
  // that no longer carry padding on any single ancestor.
  return (
    <div
      className={workspace
        ? 'flex border border-[color:var(--color-border)] rounded-md bg-white'
        : 'flex -mt-4 md:-mt-6 -mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100%+2rem)] md:min-h-[calc(100%+3rem)]'}
      aria-label="Settings"
    >
      {/* Desktop: left rail sub-nav */}
      <DesktopSettingsNav />

      {/* Content area */}
      <div className="flex-1 overflow-y-auto">
        {/* Mobile: dropdown nav */}
        <div className="p-4 pb-0 lg:p-0">
          <MobileSettingsNav />
        </div>
        <div className="p-4 md:p-6 lg:p-8 max-w-4xl">
          <RouteErrorBoundary fallbackMessage="This settings page ran into a problem." resetKey={location.pathname}>
            {allowed ? <Outlet /> : <Navigate to={href(LIMITED_LANDING)} replace />}
          </RouteErrorBoundary>
        </div>
      </div>
    </div>
  );
}
